import { useState, useEffect, useCallback } from 'react';

interface UseSearchInputOptions {
  onSearchChange: (query: string) => void;
  debounceMs?: number;
  initialValue?: string;
}

interface UseSearchInputReturn {
  searchValue: string;
  debouncedValue: string;
  handleChange: (value: string) => void;
  clearSearch: () => void;
}

/**
 * Reusable hook for search input with debouncing
 * Prevents cursor focus loss and excessive API calls
 */
export function useSearchInput({
  onSearchChange,
  debounceMs = 300,
  initialValue = '',
}: UseSearchInputOptions): UseSearchInputReturn {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  // Debounce the search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchValue);
      onSearchChange(searchValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, onSearchChange, debounceMs]);

  const handleChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setDebouncedValue('');
    onSearchChange('');
  }, [onSearchChange]);

  return {
    searchValue,
    debouncedValue,
    handleChange,
    clearSearch,
  };
}
